import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Box, Button, Typography } from '@mui/material'
import { setAlertQueue } from 'cache/alerts'
import { setIsDealLocked } from 'cache/deal-status'
import Loader from 'components/Loader/Loader'
// import VirtualAgentWidget from 'components/VirtualAgentWidget/VirtualAgentWidget'
import { useLockDeal } from 'hooks/use-deal-lock'
import { useDecisionEditPageUpdated } from 'hooks/use-decision-edit-page-updated'
import { useSaveDecisionEditPageDetails } from 'hooks/use-save-decision-edit-page-details'
import { useUnlockDealOnUnload } from 'hooks/use-unlock-deal-on-unload'
import { useUserProfile } from 'hooks/use-user-profile'
import { useClearDecisionPageCache } from 'queries/clear-decision-page-cache'
import type { GetCacheEditPageDetailsResponse } from 'queries/get-cache-edit-page-details'
import { useResetDecisionPageCache } from 'queries/reset-decision-page'
import {
  CurrencyConvertLineItem,
  useCurrencyConvertEditPage,
} from 'queries/useCurrencyConvertEditPage'
import { MessageType } from 'ts/enums/messageType.enums'
import ComparisonTabs from './components/ComparisonTabs'
import { CurrencyToggle } from './components/CurrencyToggle'
import { GridViewToggle, GridViewType } from './components/GridViewToggle'
import LinesFilterDropdown, {
  LINES_FILTER,
  LinesFilterValue,
} from './components/LinesFilter/LinesFilterDropdown'
import { ProductLineGrid } from './components/ProductLineGrid'
import { ProductsDetailsGrid } from './components/ProductsDetailsGrid'
import { transformProductsDetailsData } from './components/ProductsDetailsGrid/utils/transformProductsDetailsData'
import { PL_GUIDANCE_TYPES } from './constants'
import { useApprovalRefreshFlow } from './hooks/useApprovalRefreshFlow'
import { useDataPointSelections } from './hooks/useDataPointSelections'
import { useDealId } from './hooks/useDealId'
import { useDecisionEditPageData } from './hooks/useDecisionEditPageData'
import { useDecisionPageStorage } from './hooks/useDecisionPageStorage'
import { useStyles } from './styles/DecisionEditPage.styles'
import { getApprovalOptions } from './utils/getAllowedApprovalTypes'

// Products with authStatus 'Hold' or 'Denied' are always hidden regardless of filter.
const HIDDEN_AUTH_STATUSES: ReadonlyArray<string> = ['Hold', 'Denied']

type WrappedPlRow = NonNullable<
  GetCacheEditPageDetailsResponse['cacheEditPageDetails']['wrappedPLTotalDynamicResponse']
>['plTotalDynamicWrapped'][number]

const DecisionEditPage = () => {
  const { classes } = useStyles()
  const history = useHistory()
  const { dealNumber, decisionType, decisionReason, emailId } = useParams<{
    dealNumber: string
    decisionType: string
    decisionReason: string
    emailId: string
  }>()

  const {
    getInitialTabs,
    getInitialGuidanceSelections,
    persistGridView,
    persistIsUSDSelected,
    persistRequestedPriceTab,
    persistGuidanceSelections,
    persistLinesFilter,
    clearAllSelections,
    getInitialGridView,
    getInitialIsUSDSelected,
    getInitialLinesFilter,
  } = useDecisionPageStorage()

  const [gridView, setGridView] = useState<GridViewType>(getInitialGridView)
  const [linesFilter, setLinesFilter] = useState<LinesFilterValue | null>(getInitialLinesFilter)
  // Tracks which gridView values have already had their persisted state restored.
  // Prevents the restoration effect from re-running on every tab/guidance state change.
  // The initial gridView is pre-marked because useDataPointSelections' useState
  // initializers already seed its tabs and color map via buildInitialColorMap —
  // running the effect for it would rebuild an identical color-map object for nothing.
  const restoredGridViewsRef = useRef<Set<GridViewType>>(new Set([getInitialGridView()]))
  const [plCurrencyData, setPlCurrencyData] = useState<CurrencyConvertLineItem[] | null>(null)
  const [pnCurrencyData, setPnCurrencyData] = useState<CurrencyConvertLineItem[] | null>(null)
  const [isUSDSelected, setIsUSDSelected] = useState<boolean>(getInitialIsUSDSelected)
  const [isCurrencyLoading, setIsCurrencyLoading] = useState(false)
  const [hasUnsavedEdits, setHasUnsavedEdits] = useState(false)

  const dealId = useDealId()
  const { data, loading, refreshEditPageData } = useDecisionEditPageData(dealId)
  const { clearCache } = useClearDecisionPageCache()
  const { saveDecisionEditPageDetails, loading: saveLoading } = useSaveDecisionEditPageDetails()
  const { userId, userEmail } = useUserProfile()
  const { lockDealMutation } = useLockDeal()
  const { resetCache } = useResetDecisionPageCache()
  const { getCurrencyConversion } = useCurrencyConvertEditPage()
  const handleSaveSuccess = useCallback(() => {
    setHasUnsavedEdits(false)
  }, [])

  const { isAwaitingApprovalRefresh, executeSaveAndAwaitRefresh } = useApprovalRefreshFlow({
    dealId,
    dealNumber,
    decisionType,
    decisionReason,
    emailId,
    userId,
    userEmail,
    saveDecisionEditPageDetails,
    onSaveSuccess: handleSaveSuccess,
  })

  const handleRefreshWithCurrency = useCallback(async () => {
    await refreshEditPageData()
    if (isUSDSelected && dealId) {
      setIsCurrencyLoading(true)
      try {
        const [plResult, pnResult] = await Promise.all([
          getCurrencyConversion({
            variables: { dealId, lineTypeCode: 'PL', toCurrencyCode: 'UD' },
          }),
          getCurrencyConversion({
            variables: { dealId, lineTypeCode: 'PN', toCurrencyCode: 'UD' },
          }),
        ])
        setPlCurrencyData(plResult.data?.currencyConvertDecisionEditPage ?? null)
        setPnCurrencyData(pnResult.data?.currencyConvertDecisionEditPage ?? null)
      } catch (error) {
        console.error('Error refreshing currency conversion data:', error)
      } finally {
        setIsCurrencyLoading(false)
      }
    }
  }, [refreshEditPageData, isUSDSelected, dealId, getCurrencyConversion])

  useDecisionEditPageUpdated(dealId ?? Number(sessionStorage.getItem('dealId')), {
    onDataUpdated: handleRefreshWithCurrency,
  })

  useEffect(() => {
    persistGridView(gridView)
  }, [persistGridView, gridView])

  useEffect(() => {
    persistIsUSDSelected(isUSDSelected)
  }, [persistIsUSDSelected, isUSDSelected])

  useEffect(() => {
    persistLinesFilter(linesFilter)
  }, [persistLinesFilter, linesFilter])

  // Lock on mount; skip if arriving from the decision page (deal is already locked).
  // Re-locks after a page refresh when the sessionStorage flag is absent.
  useEffect(() => {
    if (!dealId || !userId) return

    setIsDealLocked(true)
    const fromDecisionPage = sessionStorage.getItem('fromDecisionPage') === 'true'
    sessionStorage.removeItem('fromDecisionPage')

    if (fromDecisionPage) return

    lockDealMutation(dealId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, userId])

  useUnlockDealOnUnload({ dealId, userId })

  const dealCubeHistoryDetails = useMemo(
    () => data?.dealCubeHistoryProductDetails?.dealCubeHistoryDetails ?? [],
    [data?.dealCubeHistoryProductDetails?.dealCubeHistoryDetails]
  )
  const approvalOptions = useMemo(
    () => getApprovalOptions(dealCubeHistoryDetails),
    [dealCubeHistoryDetails]
  )

  const {
    currentActiveTabs,
    currentApprovalSelections,
    currentGuidanceSelections,
    currentColorMap,
    currentIsLimitReached,
    plActiveTabs,
    pnActiveTabs,
    sanitizedPlApprovalSelections,
    sanitizedPnApprovalSelections,
    plGuidanceSelections,
    pnGuidanceSelections,
    plResolvedColorMap,
    pnResolvedColorMap,
    isSelectionsEmpty,
    handleTabsChange,
    handleGuidanceTypesChange,
    handleApprovalTypesChange,
    restorePersistedState,
    resetSelections,
  } = useDataPointSelections(gridView, approvalOptions, {
    initialTabs: getInitialTabs(gridView),
    initialGuidanceSelections: getInitialGuidanceSelections(gridView),
  })

  useEffect(() => {
    // Only restore persisted state once per gridView. Without this guard, every tab
    // or guidance state change recreates handleTabsChange/replaceGuidanceSelections
    // (they are useCallbacks with reactive deps), re-triggering this effect and:
    //   1. Re-adding 'requestedPrice' even after the user explicitly deselects it.
    //   2. Calling replaceGuidanceSelections with stale storage values on every
    //      guidance selection, rebuilding the color map and flickering column defs.
    if (restoredGridViewsRef.current.has(gridView)) return
    restoredGridViewsRef.current.add(gridView)

    const restoredTabs = getInitialTabs(gridView)
    const restoredGuidance = getInitialGuidanceSelections(gridView)
    // Restore tabs + guidance + colorMap in one atomic call so the rebuilt color
    // map includes every key (requestedPrice + guidance slots) from the start.
    // Sequential handleTabsChange → replaceGuidanceSelections would race: each
    // reads stale closure state and the last setColorMap call wins, dropping the
    // slot written by the first (the requestedPrice color disappears).
    restorePersistedState({ tabs: restoredTabs, guidance: restoredGuidance })
  }, [getInitialGuidanceSelections, getInitialTabs, gridView, restorePersistedState])

  useEffect(() => {
    persistRequestedPriceTab(gridView, currentActiveTabs.includes('requestedPrice'))
  }, [currentActiveTabs, gridView, persistRequestedPriceTab])

  useEffect(() => {
    const selections = gridView === 'PL' ? plGuidanceSelections : pnGuidanceSelections
    persistGuidanceSelections(gridView, selections)
  }, [plGuidanceSelections, pnGuidanceSelections, gridView, persistGuidanceSelections])

  // Filter wrappedCombinedFlatProducts:
  //  1. Always exclude products with authStatus 'Hold' or 'Denied'
  //  2. Apply lines filter: 'all' → isAllLine, 'open' → isOpenLine, 'my' → isMyLine
  const filteredFlatProducts = useMemo(() => {
    const products = data?.wrappedCombinedFlatProducts
    if (!products) return undefined
    return products.filter((p) => {
      const { authStatus, isAllLine, isOpenLine, isMyLine } = p.product
      if (authStatus && HIDDEN_AUTH_STATUSES.includes(authStatus)) return false
      if (linesFilter === LINES_FILTER.OPEN) return isOpenLine === true
      if (linesFilter === LINES_FILTER.MY) return isMyLine === true
      if (linesFilter === LINES_FILTER.ALL) return isAllLine === true
      // null — no filter selected, show all
      return true
    })
  }, [data?.wrappedCombinedFlatProducts, linesFilter])

  // Derive the set of PL codes visible after filtering, for use by ProductLineGrid.
  // Always computed from filteredFlatProducts so authStatus exclusions are respected.
  const visiblePlCodes = useMemo(() => {
    if (!filteredFlatProducts) return null
    return new Set(filteredFlatProducts.map((p) => p.product.assosiatePlCode))
  }, [filteredFlatProducts])

  const hasPlLineFlags = useCallback(
    (pl: WrappedPlRow): boolean => {
      if (!pl.busGroupCode) return false
      if (linesFilter === LINES_FILTER.OPEN) return pl.isOpenLine !== null
      if (linesFilter === LINES_FILTER.MY) return pl.isMyLine !== null
      return false
    },
    [linesFilter]
  )

  // For open/my filters, derive which PL codes have at least one cost-available PN.
  // Used by ProductLineGrid to render editable vs dotted-readonly cells per row.
  const plCostAvailableSet = useMemo<Set<string> | null>(() => {
    if (linesFilter !== LINES_FILTER.OPEN && linesFilter !== LINES_FILTER.MY) return null
    const plRows = data?.wrappedPLTotalDynamicResponse?.plTotalDynamicWrapped ?? []
    const plRowsWithFlags = plRows.filter(hasPlLineFlags)

    if (plRowsWithFlags.length > 0) {
      const set = new Set<string>()
      plRowsWithFlags.forEach((pl) => {
        const isVisible = linesFilter === LINES_FILTER.OPEN ? pl.isOpenLine : pl.isMyLine
        if (isVisible === true && pl.isCostAvailable === true) {
          set.add(pl.productLineCode)
        }
      })
      return set
    }

    if (!filteredFlatProducts) return null
    const set = new Set<string>()
    filteredFlatProducts.forEach((p) => {
      if (p.product.authStatus === 'Open' && p.product.isCostAvailable === true) {
        set.add(p.product.assosiatePlCode)
      }
    })
    return set
  }, [
    data?.wrappedPLTotalDynamicResponse?.plTotalDynamicWrapped,
    filteredFlatProducts,
    hasPlLineFlags,
    linesFilter,
  ])

  const pnData = useMemo(
    () =>
      filteredFlatProducts
        ? transformProductsDetailsData(filteredFlatProducts, dealCubeHistoryDetails, {
            totalBdNetPriceAmount: data?.totalBdNetPriceAmount,
            totalEstimatedQuantity: data?.totalEstimatedQuantity,
          })
        : null,
    [
      filteredFlatProducts,
      dealCubeHistoryDetails,
      data?.totalBdNetPriceAmount,
      data?.totalEstimatedQuantity,
    ]
  )

  const modifiedLineNumbers = useMemo(
    () =>
      new Set<string>(
        (data?.wrappedCombinedFlatProducts ?? [])
          .filter((wrapped) => wrapped.isModified)
          .map((wrapped) => String(wrapped.product.lineNumber))
      ),
    [data?.wrappedCombinedFlatProducts]
  )

  const modifiedPlCodes = useMemo(
    () =>
      new Set<string>(
        (data?.wrappedPLTotalDynamicResponse?.plTotalDynamicWrapped ?? [])
          .filter((pl) => pl.isModified)
          .map((pl) => pl.productLineCode)
      ),
    [data?.wrappedPLTotalDynamicResponse?.plTotalDynamicWrapped]
  )

  const plData = useMemo(() => {
    const items = data?.wrappedPLTotalDynamicResponse?.plTotalDynamicWrapped
    if (!items) return undefined
    // PL totals come from a separate API response; only intersect with visible product codes for Open/My.
    if (linesFilter !== LINES_FILTER.OPEN && linesFilter !== LINES_FILTER.MY) return items
    const itemsWithPlLineFlags = items.filter(hasPlLineFlags)

    const visibleProductLineItems =
      itemsWithPlLineFlags.length > 0
        ? itemsWithPlLineFlags.filter((pl) =>
            linesFilter === LINES_FILTER.OPEN ? pl.isOpenLine === true : pl.isMyLine === true
          )
        : visiblePlCodes
          ? items.filter((pl) => visiblePlCodes.has(pl.productLineCode))
          : items

    const visiblePlCodeSet = new Set(visibleProductLineItems.map((pl) => pl.productLineCode))

    // Build per-category rollup from visible PL rows only, so category header totals
    // reconcile with the visible child rows rather than showing server-computed totals
    // that span all PLs in the category (including non-visible ones).
    type CategoryRollup = {
      estimatedPnBdNetRevenue: number
      weightedEstimatedDiscountPercentage: number
      weightedEstimatedDiscountPercentageUpdated: number | null
      weightedCm: number | null
      weightedUpdatedCm: number | null
      weightedExpertDiscountPercentage: number | null
      weightedFloorDiscountPercentage: number | null
      weightedTypicalDiscountPercentage: number | null
    }

    const weightedAverage = (
      rows: WrappedPlRow[],
      getValue: (row: WrappedPlRow) => number | null | undefined,
      fallback: number | null
    ): number | null => {
      const rowsWithValue = rows.filter(
        (row) => getValue(row) !== null && getValue(row) !== undefined
      )
      if (!rowsWithValue.length) return fallback

      const weight = rowsWithValue.reduce(
        (total, row) => total + (row.estimatedPnBdNetRevenue ?? 0),
        0
      )
      if (weight <= 0) {
        return Number(
          (
            rowsWithValue.reduce((total, row) => total + (getValue(row) ?? 0), 0) /
            rowsWithValue.length
          ).toFixed(6)
        )
      }

      const weightedSum = rowsWithValue.reduce(
        (total, row) => total + (getValue(row) ?? 0) * (row.estimatedPnBdNetRevenue ?? 0),
        0
      )
      return Number((weightedSum / weight).toFixed(6))
    }
    const categoryRowsMap = new Map<string, typeof visibleProductLineItems>()
    visibleProductLineItems.forEach((pl) => {
      if (!pl.plCategoryCd) return
      const rows = categoryRowsMap.get(pl.plCategoryCd)
      if (rows) {
        rows.push(pl)
      } else {
        categoryRowsMap.set(pl.plCategoryCd, [pl])
      }
    })

    const categoryRollupMap = new Map<string, CategoryRollup>()
    categoryRowsMap.forEach((categoryRows, categoryCd) => {
      categoryRollupMap.set(categoryCd, {
        estimatedPnBdNetRevenue: categoryRows.reduce(
          (total, row) => total + (row.estimatedPnBdNetRevenue ?? 0),
          0
        ),
        weightedEstimatedDiscountPercentage:
          weightedAverage(categoryRows, (row) => row.estimatedDiscountPercentage, 0) ?? 0,
        weightedEstimatedDiscountPercentageUpdated: weightedAverage(
          categoryRows,
          (row) => row.estimatedDiscountPercentageUpdated,
          null
        ),
        weightedCm: weightedAverage(categoryRows, (row) => row.cm, null),
        weightedUpdatedCm: weightedAverage(categoryRows, (row) => row.updatedCm, null),
        weightedExpertDiscountPercentage: weightedAverage(
          categoryRows,
          (row) => row.expertDiscountPercentage,
          null
        ),
        weightedFloorDiscountPercentage: weightedAverage(
          categoryRows,
          (row) => row.floorDiscountPercentage,
          null
        ),
        weightedTypicalDiscountPercentage: weightedAverage(
          categoryRows,
          (row) => row.typicalDiscountPercentage,
          null
        ),
      })
    })

    const result: typeof items = []
    items.forEach((pl) => {
      if (visiblePlCodeSet.has(pl.productLineCode)) {
        result.push(pl)
        return
      }

      // Keep category header (aggregate) rows whose category still has at least one
      // visible PL, so the category name renders correctly in Open/My views.
      const isCategoryAggregateRow = pl.busGroupCode === ''
      if (!isCategoryAggregateRow || !pl.plCategoryCd) return

      const rollup = categoryRollupMap.get(pl.plCategoryCd)
      if (!rollup) return

      // Replace server-side category totals with values derived from visible PL rows only.
      result.push({
        ...pl,
        estimatedPnBdNetRevenue: rollup.estimatedPnBdNetRevenue,
        estimatedDiscountPercentage: rollup.weightedEstimatedDiscountPercentage,
        estimatedDiscountPercentageUpdated: rollup.weightedEstimatedDiscountPercentageUpdated,
        cm: rollup.weightedCm,
        updatedCm: rollup.weightedUpdatedCm,
        expertDiscountPercentage: rollup.weightedExpertDiscountPercentage,
        floorDiscountPercentage: rollup.weightedFloorDiscountPercentage,
        typicalDiscountPercentage: rollup.weightedTypicalDiscountPercentage,
      })
    })
    return result
  }, [
    data?.wrappedPLTotalDynamicResponse?.plTotalDynamicWrapped,
    hasPlLineFlags,
    linesFilter,
    visiblePlCodes,
  ])

  const plTotals = data?.wrappedPLTotalDynamicResponse
    ? {
        totalNetRevenue: data.wrappedPLTotalDynamicResponse.totalNetRevenue,
        totalDiscountPercentage: data.wrappedPLTotalDynamicResponse.totalDiscountPercentage,
        totalOGMPercentage: data.wrappedPLTotalDynamicResponse.totalOGMPercentage,
      }
    : undefined

  const dealCurrency = data?.dealHeaderInformation?.isoCurrencyCode ?? 'EUR'

  const handleCurrencyDataChange = useCallback(
    (
      plData: CurrencyConvertLineItem[] | null,
      pnData: CurrencyConvertLineItem[] | null,
      isUSD: boolean
    ) => {
      setPlCurrencyData(plData)
      setPnCurrencyData(pnData)
      setIsUSDSelected(isUSD)
    },
    []
  )

  const handleCurrencyLoadingChange = useCallback((isLoading: boolean) => {
    setIsCurrencyLoading(isLoading)
  }, [])

  const handleCancelClick = useCallback(() => {
    const redirectPath = `/workflow/${dealNumber}/${decisionType}/${decisionReason}/${emailId}`

    if (!hasUnsavedEdits) {
      history.push(redirectPath)
      return
    }

    setAlertQueue({
      type: MessageType.INFO,
      message: 'All the changes will be removed. Are you sure you want to cancel all the changes?',
      onOk: async () => {
        if (dealId) {
          await clearCache({ variables: { dealId } })
          history.push(redirectPath)
        }
      },
    })
  }, [
    clearCache,
    dealId,
    history,
    dealNumber,
    decisionType,
    decisionReason,
    emailId,
    hasUnsavedEdits,
  ])

  const handleSaveAndApply = () => {
    setAlertQueue({
      type: MessageType.INFO,
      message: 'Are you sure you want to save and apply all the changes?',
      onOk: () => {
        executeSaveAndAwaitRefresh()
      },
    })
  }

  const isAllReset = useMemo(
    () =>
      !hasUnsavedEdits &&
      plGuidanceSelections.length === 0 &&
      pnGuidanceSelections.length === 0 &&
      !currentActiveTabs.includes('requestedPrice') &&
      isSelectionsEmpty &&
      plCurrencyData === null &&
      pnCurrencyData === null &&
      !isUSDSelected,
    [
      hasUnsavedEdits,
      isSelectionsEmpty,
      plGuidanceSelections,
      pnGuidanceSelections,
      currentActiveTabs,
      plCurrencyData,
      pnCurrencyData,
      isUSDSelected,
    ]
  )

  const handleResetClick = useCallback(async () => {
    if (!dealId || !userEmail) return

    try {
      const result = await resetCache({ variables: { dealId, currentUserEmail: userEmail } })
      if (result.data?.resetDecisionPage?.isSuccess) {
        setHasUnsavedEdits(false)
        setGridView('PL')
        setLinesFilter(LINES_FILTER.ALL)
        resetSelections()
        setPlCurrencyData(null)
        setPnCurrencyData(null)
        setIsUSDSelected(false)
        clearAllSelections()
        await refreshEditPageData()
      } else {
        setAlertQueue({
          type: MessageType.ERROR,
          message: `Failed to reset decision page data for deal ${dealNumber}. Please try again.`,
        })
      }
    } catch (error) {
      console.error('Error resetting decision page cache:', error)
      setAlertQueue({
        type: MessageType.ERROR,
        message: `An error occurred while resetting decision page data for deal  ${dealNumber}. Please try again.`,
      })
    }
  }, [
    resetCache,
    dealId,
    userEmail,
    refreshEditPageData,
    dealNumber,
    resetSelections,
    clearAllSelections,
  ])

  return (
    <Box className={classes.mainContainer}>
      <Loader open={loading || isCurrencyLoading || saveLoading || isAwaitingApprovalRefresh} />
      <Box>
        <Typography className={classes.title}>Business Pricing Solution</Typography>
        {/* NOTE: The Virtual Agent component is currently disabled.
                  This feature will be released as part of the Warranty release
                  Until then, it will remain commented out.
        */}
        {/* <VirtualAgentWidget /> */}
        <Box className={classes.container}>
          <Box className={classes.toolbar}>
            <GridViewToggle value={gridView} onChange={setGridView} />
            <CurrencyToggle
              dealCurrency={dealCurrency}
              checked={isUSDSelected}
              fetchOnMount={isUSDSelected}
              onCurrencyDataChange={handleCurrencyDataChange}
              onLoadingChange={handleCurrencyLoadingChange}
            />
            <Box className={classes.tabsContainer}>
              <Typography className={classes.dataPointsLabel}>Data Points: </Typography>
              <ComparisonTabs
                activeTabs={currentActiveTabs}
                onTabChange={handleTabsChange}
                selectedApprovalTypes={currentApprovalSelections}
                onApprovalTypesChange={handleApprovalTypesChange}
                selectedGuidanceTypes={currentGuidanceSelections}
                onGuidanceTypesChange={handleGuidanceTypesChange}
                approvalOptions={approvalOptions}
                allowedGuidanceTypes={gridView === 'PL' ? PL_GUIDANCE_TYPES : undefined}
                colorMap={currentColorMap}
                isLimitReached={currentIsLimitReached}
              />
            </Box>
            <LinesFilterDropdown
              value={linesFilter}
              onChange={setLinesFilter}
              buttonClassName={classes.filteringButton}
              paperClassName={classes.filteringDropdownPaper}
              menuItemClassName={classes.filteringMenuItem}
            />
            <Button
              className={classes.resetBtn}
              onClick={handleResetClick}
              disabled={saveLoading || isAllReset}
            >
              Reset All
            </Button>
          </Box>
          {gridView === 'PL' ? (
            <ProductLineGrid
              dealId={dealId}
              data={plData}
              totals={plTotals}
              currencyData={plCurrencyData ?? undefined}
              isUSDSelected={isUSDSelected}
              dealCurrency={dealCurrency}
              activeTabs={plActiveTabs}
              selectedApprovalTypes={sanitizedPlApprovalSelections}
              selectedGuidanceTypes={plGuidanceSelections}
              colorMap={plResolvedColorMap}
              approvalOptions={approvalOptions}
              dealCubeHistoryDetails={dealCubeHistoryDetails}
              linesFilter={linesFilter ?? undefined}
              plCostAvailableSet={plCostAvailableSet ?? undefined}
              onRefresh={refreshEditPageData}
              onEditSuccess={() => setHasUnsavedEdits(true)}
              modifiedPlCodes={modifiedPlCodes}
            />
          ) : (
            <ProductsDetailsGrid
              data={pnData}
              currencyData={pnCurrencyData ?? undefined}
              isUSDSelected={isUSDSelected}
              dealCurrency={dealCurrency}
              activeTabs={pnActiveTabs}
              selectedApprovalTypes={sanitizedPnApprovalSelections}
              selectedGuidanceTypes={pnGuidanceSelections}
              colorMap={pnResolvedColorMap}
              approvalOptions={approvalOptions}
              dealId={dealId}
              linesFilter={linesFilter ?? undefined}
              onRefresh={refreshEditPageData}
              onEditSuccess={() => setHasUnsavedEdits(true)}
              modifiedLineNumbers={modifiedLineNumbers}
            />
          )}
        </Box>
        <Box className={classes.actionsContainer}>
          <Button
            className={classes.actionBtn}
            variant="outlined"
            color="primary"
            onClick={handleCancelClick}
          >
            Cancel
          </Button>
          <Button
            className={classes.actionBtn}
            variant="contained"
            color="primary"
            disabled={isUSDSelected || saveLoading || !hasUnsavedEdits}
            onClick={handleSaveAndApply}
          >
            Save and apply
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default DecisionEditPage
